import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  ShoppingCart, Loader2, Timer, MessageSquareWarning,
  Send, Paperclip, X, ImageUp, Square, Sparkles,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export const MOD_PACKS = [
  { count: 10, usd: 10, uyu: 400 },
  { count: 20, usd: 20, uyu: 750 },
  { count: 30, usd: 30, uyu: 1100 },
  { count: 50, usd: 50, uyu: 1800 },
];

// ─── Countdown hook ───────────────────────────────────────────────────────────
export function useCountdown(targetIso) {
  const [secs, setSecs] = useState(0);
  useEffect(() => {
    if (!targetIso) return;
    const update = () => {
      const diff = Math.max(0, Math.ceil((new Date(targetIso) - Date.now()) / 1000));
      setSecs(diff);
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  return secs;
}

function fmtSecs(s) {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m ${String(r).padStart(2, "0")}s`;
}

// ─── Credits Exhausted → Buy Packs ───────────────────────────────────────────
export function CreditsExhausted({ token }) {
  const [loading, setLoading] = useState(null); // pack.count while loading
  const [errorMsg, setErrorMsg] = useState("");

  const buyPack = async (pack) => {
    setLoading(pack.count);
    setErrorMsg("");
    try {
      const res = await fetch(`${API}/client/${token}/mods/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pack: pack.count, currency: "USD", origin_url: window.location.origin }),
      });
      const d = await res.json();
      if (d.checkout_url) {
        window.location.href = d.checkout_url;
      } else {
        setErrorMsg(d.detail || "No se pudo iniciar el pago.");
      }
    } catch {
      setErrorMsg("Error de conexión. Intentá de nuevo.");
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="mx-3 mb-3" data-testid="upsell-panel">
      <div className="rounded-2xl overflow-hidden border border-white/10 bg-[#0A0A0A]">

        {/* Header */}
        <div className="px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2 mb-0.5">
            <ShoppingCart size={13} className="text-[#ffc700]" />
            <p className="text-xs font-black text-white">Comprá más modificaciones</p>
          </div>
          <p className="text-[11px] text-gray-400">
            Pago único · Sin suscripción · Los créditos no vencen
          </p>
        </div>

        {/* Packs grid */}
        <div className="px-4 py-3">
          {errorMsg && (
            <p className="text-[10px] text-red-400 mb-2 text-center">{errorMsg}</p>
          )}

          <div className="grid grid-cols-2 gap-2">
            {MOD_PACKS.map(pack => (
              <button
                key={pack.count}
                data-testid={`pack-${pack.count}-btn`}
                onClick={() => buyPack(pack)}
                disabled={!!loading}
                className="flex flex-col items-center gap-0.5 py-3 rounded-xl border border-gray-700 hover:border-[#ffc700] hover:bg-[#ffc700]/5 transition-all disabled:opacity-50"
              >
                {loading === pack.count ? (
                  <Loader2 size={16} className="animate-spin text-[#ffc700]" />
                ) : (
                  <>
                    <span className="text-2xl font-black text-white">{pack.count}</span>
                    <span className="text-[10px] text-gray-500">modificaciones</span>
                    <span className="text-xs font-black text-[#ffc700] mt-0.5">USD ${pack.usd}</span>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Rate Limit Bar ───────────────────────────────────────────────────────────
export function RateLimitBar({ rl }) {
  const waitSecs = useCountdown(rl?.is_limited ? rl.reset_at : null);
  if (!rl) return null;

  if (rl.is_limited) {
    return (
      <div className="mx-4 mb-3 bg-[#0A0A0A] text-white rounded-2xl p-4">
        <div className="flex items-center gap-2.5 mb-2">
          <Timer size={15} className="text-[#ffc700]" />
          <p className="text-sm font-black">Límite de mensajes alcanzado</p>
        </div>
        <p className="text-xs text-gray-400 mb-2">Podés enviar hasta 10 mensajes por hora.</p>
        <div className="bg-white/10 rounded-xl px-4 py-3 text-center">
          <p className="text-2xl font-black text-[#ffc700]">{fmtSecs(waitSecs)}</p>
          <p className="text-[10px] text-gray-400 mt-0.5">hasta el próximo mensaje</p>
        </div>
      </div>
    );
  }

  if (rl.remaining <= 3 && rl.remaining > 0) {
    return (
      <div className="mx-4 mb-2 flex items-start gap-2.5 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
        <MessageSquareWarning size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-xs text-amber-700">
          <strong>Atención:</strong> Solo te quedan <strong>{rl.remaining}</strong> mensajes esta hora. Al agotarlos, deberás esperar 60 minutos.
        </p>
      </div>
    );
  }

  return null;
}

// ─── Chat Input ───────────────────────────────────────────────────────────────
export function ChatInput({
  value, onChange, onSend, disabled, hasPending, isLimited,
  attachedImage, onImageAttach, onImageRemove,
  siteImage, onSiteImageAttach, onSiteImageRemove,
  history = [],          // recent user messages, newest first
  onStop,                // optional: cancel an in-flight request
  onSuggest,             // optional: ask AI for proactive improvements
}) {
  const aiFileRef = useRef(null);
  const siteFileRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  // History cursor — -1 means "current draft", 0..n indexes into history.
  // Resets when the user types or sends a message.
  const [histIdx, setHistIdx] = useState(-1);
  const draftRef = useRef("");

  const placeholder = hasPending
    ? "Podés refinar tu pedido o aceptar los cambios propuestos..."
    : isLimited ? "Esperá para enviar otro mensaje..."
    : "Describí el cambio que querés hacer en tu sitio...";

  const processFile = (file, isSiteImage = false) => {
    if (!file || !file.type.startsWith("image/")) { toast.error("Solo se aceptan imágenes."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("La imagen no puede superar 10MB"); return; }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result.split(",")[1];
      const payload = { base64, mime: file.type || "image/jpeg", preview: ev.target.result, name: file.name };
      if (isSiteImage) onSiteImageAttach(payload);
      else onImageAttach(payload);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => { e.preventDefault(); setIsDragging(true); };
  const handleDragLeave = () => setIsDragging(false);
  const handleDrop = (e) => {
    e.preventDefault(); setIsDragging(false);
    processFile(e.dataTransfer.files?.[0]);
  };
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      onSend();
      // Reset history navigation after send
      setHistIdx(-1);
      draftRef.current = "";
      return;
    }
    // ↑ / ↓ — navigate command history (only when caret is at the start
    // and the textarea is single-line — same UX as a terminal prompt).
    if ((e.key === "ArrowUp" || e.key === "ArrowDown") && history.length > 0) {
      const ta = e.target;
      const isMultiline = (value || "").includes("\n");
      const atStart = ta.selectionStart === 0 && ta.selectionEnd === 0;
      const atEnd = ta.selectionStart === (value || "").length;
      if (isMultiline && !(e.key === "ArrowUp" ? atStart : atEnd)) return;
      e.preventDefault();
      let next = histIdx;
      if (e.key === "ArrowUp") {
        if (next === -1) draftRef.current = value || "";
        next = Math.min(history.length - 1, next + 1);
      } else {
        next = Math.max(-1, next - 1);
      }
      setHistIdx(next);
      onChange(next === -1 ? draftRef.current : history[next]);
    }
  };

  return (
    <div
      className={`border-t transition-colors relative ${isDragging ? "border-[#ffc700] bg-[#ffc700]/5" : "border-[#1a1a1a] bg-[#0A0A0A]"}`}
      onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 z-20 flex items-center justify-center pointer-events-none">
          <div className="bg-[#ffc700] text-black font-black text-sm px-6 py-3 rounded-2xl shadow-2xl">Soltá la imagen aquí</div>
        </div>
      )}

      {/* Attached images row */}
      {(attachedImage || siteImage) && (
        <div className="px-3 pt-2.5 flex flex-wrap gap-2">
          {/* AI reference image */}
          {attachedImage && (
            <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-xl px-2.5 py-1.5">
              <img src={attachedImage.preview} alt="ref" className="w-8 h-8 rounded-lg object-cover" />
              <div className="flex flex-col leading-tight">
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-wider">Referencia IA</span>
                <span className="text-xs text-gray-300 font-medium max-w-[100px] truncate">{attachedImage.name}</span>
              </div>
              <button onClick={onImageRemove} className="text-gray-500 hover:text-white"><X size={12} /></button>
            </div>
          )}
          {/* Site-use image */}
          {siteImage && (
            <div className="inline-flex items-center gap-2 bg-green-900/30 border border-green-500/40 rounded-xl px-2.5 py-1.5">
              <img src={siteImage.preview} alt="site" className="w-8 h-8 rounded-lg object-cover ring-1 ring-green-400/50" />
              <div className="flex flex-col leading-tight">
                <span className="text-[9px] font-bold text-green-400 uppercase tracking-wider">Para el sitio</span>
                <span className="text-xs text-gray-300 font-medium max-w-[100px] truncate">{siteImage.name}</span>
              </div>
              <button onClick={onSiteImageRemove} className="text-green-500/70 hover:text-green-400"><X size={12} /></button>
            </div>
          )}
        </div>
      )}

      {/* Textarea */}
      <textarea
        data-testid="chat-input"
        value={value}
        onChange={e => onChange(e.target.value)}
        onKeyDown={handleKey}
        disabled={disabled || isLimited}
        placeholder={placeholder}
        rows={3}
        style={{ resize: "none", minHeight: "88px", maxHeight: "200px" }}
        onInput={e => { e.target.style.height = "auto"; e.target.style.height = Math.min(200, e.target.scrollHeight) + "px"; }}
        className="w-full bg-transparent px-4 pt-3 pb-2 text-[13px] text-gray-200 placeholder-gray-600 focus:outline-none disabled:opacity-40 leading-relaxed"
      />

      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 pb-3">
        <div className="flex items-center gap-1.5">
          {/* Clip: attach for AI reference */}
          <button type="button" onClick={() => aiFileRef.current?.click()}
            disabled={disabled || isLimited} data-testid="attach-image-btn"
            title="Adjuntar imagen como referencia visual para la IA"
            className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-600 hover:text-[#ffc700] hover:bg-white/[0.06] transition-all disabled:opacity-30">
            <Paperclip size={15} />
          </button>
          <input ref={aiFileRef} type="file" accept="image/*" className="hidden" onChange={e => { processFile(e.target.files?.[0], false); e.target.value = ""; }} />

          {/* ImageUp: upload to use in site */}
          <button type="button" onClick={() => siteFileRef.current?.click()}
            disabled={disabled || isLimited} data-testid="upload-site-image-btn"
            title="Subir foto para usar en el sitio web"
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-all disabled:opacity-30 ${siteImage ? "text-green-400 bg-green-400/10" : "text-gray-600 hover:text-green-400 hover:bg-green-400/10"}`}>
            <ImageUp size={15} />
          </button>
          <input ref={siteFileRef} type="file" accept="image/*" className="hidden" onChange={e => { processFile(e.target.files?.[0], true); e.target.value = ""; }} />

          <span className="text-[10px] text-gray-700 ml-1">↵ Enviar · Shift+↵ nueva línea</span>
        </div>
        <div className="flex items-center gap-1.5">
          {/* Suggest button — copilot-style: ask the AI for proactive
              improvements without typing anything. Hidden during loading. */}
          {onSuggest && !disabled && (
            <button
              type="button"
              onClick={onSuggest}
              disabled={isLimited}
              data-testid="suggest-improvements-btn"
              title="Pedile a la IA que sugiera mejoras automáticamente"
              className="flex items-center gap-1 px-2.5 py-2 text-[11px] font-bold text-[#ffc700] bg-[#ffc700]/10 hover:bg-[#ffc700]/20 border border-[#ffc700]/30 rounded-xl transition-all disabled:opacity-40 active:scale-95"
            >
              <Sparkles size={12} />
              Sugerir mejoras
            </button>
          )}
          {/* Stop button — visible only while a request is in flight.
              Cancels the AbortController in EmbeddedAIChat. Ported from
              grapesjs-ai-copilot's renderLoading "Stop" link. */}
          {disabled && onStop && (
            <button
              type="button"
              onClick={onStop}
              data-testid="stop-chat-btn"
              title="Cancelar la consulta a la IA"
              className="flex items-center gap-1 px-3 py-2 text-[11px] font-bold text-red-400 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 rounded-xl transition-all active:scale-95"
            >
              <Square size={11} fill="currentColor" />
              Detener
            </button>
          )}
          <button onClick={() => onSend()}
            disabled={disabled || (!value.trim() && !attachedImage && !siteImage) || isLimited}
            data-testid="send-chat-btn"
            className="flex items-center gap-1.5 px-4 py-2 bg-[#ffc700] text-black text-xs font-black rounded-xl disabled:opacity-30 hover:bg-yellow-400 transition-all active:scale-95">
            {disabled ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            {disabled ? "Pensando..." : "Enviar"}
          </button>
        </div>
      </div>
    </div>
  );
}
